import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Crown, Sparkles, Trophy, Ticket, ChevronRight, Flame, Anchor, Waves, Award, Compass } from "lucide-react";
import { CountdownTimer } from "@/components/CountdownTimer";
import { BoardingForm } from "@/components/BoardingForm";
import { Layout } from "@/components/Layout";
import { ArmadaSectorList } from "@/components/ArmadaSectorList";
import { Wingman } from "@/components/Wingman";
import { api } from "@/lib/api";
import { LANDING } from "@/constants/testIds";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/f096cfa9-2798-44b8-94e2-48fecfa7026b/images/beb199a0ad2ac44a0b02ef555b499e358b06997cb3bc34a01ac73685aba4d197.png";
const GOLD_ABSTRACT = "https://static.prod-images.emergentagent.com/jobs/f096cfa9-2798-44b8-94e2-48fecfa7026b/images/ae8ab3b08775807e81d7056aa61c8b4b63b59f351a0483f92c702743d52c16bc.png";
const SKIN_PORTRAIT = "https://images.pexels.com/photos/26606931/pexels-photo-26606931.jpeg";
const KIT_IMAGE = "https://static.prod-images.emergentagent.com/jobs/f096cfa9-2798-44b8-94e2-48fecfa7026b/images/6c07e040a32c38ed3068a76fa4f63bf999957372c6a4dc83605c3c8e506addd4.png";

export default function Landing() {
  const [boardingOpen, setBoardingOpen] = useState(false);
  const [contest, setContest] = useState(null);
  const [leaders, setLeaders] = useState([]);

  useEffect(() => {
    api.get("/contest/info").then((r) => setContest(r.data)).catch(() => {});
    api.get("/leaderboard").then((r) => setLeaders(r.data)).catch(() => {});
  }, []);

  return (
    <Layout onApply={() => setBoardingOpen(true)}>
      <BoardingForm open={boardingOpen} onClose={() => setBoardingOpen(false)} />
      <Wingman />

      {/* HERO */}
      <section className="relative min-h-[100svh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/55 to-black"/>
          <div className="absolute inset-0 grain"/>
        </div>
        <div className="relative px-6 md:px-12 lg:px-20 py-24 max-w-6xl">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <div className="inline-flex items-center gap-2 border border-[#C9A227]/40 bg-black/40 backdrop-blur px-4 py-2 mb-8">
              <Sparkles className="text-[#C9A227]" size={14}/>
              <span className="text-[10px] tracking-[0.3em] uppercase text-[#C9A227] font-bold">Casting Call · EIGOO Standard</span>
            </div>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.1 }}
            className="text-5xl sm:text-6xl lg:text-8xl font-black uppercase tracking-tighter text-white leading-[0.92]">
            The Search<br/>
            <span className="gold-shimmer">for the Siren</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.4 }}
            className="mt-6 text-base md:text-lg text-white/70 max-w-2xl leading-relaxed">
            EIGOO has the architecture ready. We are searching for the <span className="text-white font-semibold">Authority of Pacific Beauty</span> to command this infrastructure — a Mililani-forged Admiral fluent in Bratz-clean skin doctrine, with the algorithm dominion to claim her territory.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.6 }}
            className="mt-10 flex flex-wrap items-center gap-4">
            <button onClick={() => setBoardingOpen(true)} className="btn-gold pulse-gold" data-testid={LANDING.applyHeroBtn}>
              Apply for Admiral Commission
            </button>
            <Link to="/leaderboard" className="btn-outline-gold">
              View Candidates <ChevronRight size={14} className="inline -mt-0.5"/>
            </Link>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.9 }} className="mt-16">
            <CountdownTimer deadline={contest?.deadline} label="Boarding Closes In" />
          </motion.div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 border-t border-[#C9A227]/15 bg-black/60 backdrop-blur py-3 overflow-hidden">
          <div className="flex gap-12 marquee-track whitespace-nowrap">
            {["track-a", "track-b"].map((trackKey) => (
              <div key={trackKey} className="flex gap-12 px-6">
                <span className="text-[#C9A227] text-xs tracking-[0.3em] uppercase">Mililani, Hawai‘i · UV Index 11</span>
                <span className="text-white/30 text-xs tracking-[0.3em] uppercase">75 / 15 / 10 Lineage Law</span>
                <span className="text-[#C9A227] text-xs tracking-[0.3em] uppercase">Bratz Clean Skin Doctrine</span>
                <span className="text-white/30 text-xs tracking-[0.3em] uppercase">$99 Boarding Deposit · Refundable</span>
                <span className="text-[#C9A227] text-xs tracking-[0.3em] uppercase">Armada · 10 Sectors Active</span>
                <span className="text-white/30 text-xs tracking-[0.3em] uppercase">EIGOO Standard · v1.0</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* THE PRIZE */}
      <section className="relative py-24 lg:py-32 px-6 md:px-12 lg:px-20">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <Trophy className="text-[#C9A227]"/>
            <span className="label-gold">The Prize</span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black uppercase tracking-tighter text-white mb-12 max-w-3xl">
            What the Chosen <span className="gold-shimmer">Admiral</span> Receives.
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <PrizeCard icon={<Crown size={28}/>} title="The Flagship Hull"
              description="Full operational ownership of Pacific Siren v1.0 — storefront, stream, college, social command, and admiral dashboard."
              tag="Equity Asset" />
            <PrizeCard icon={<Award size={28}/>} title="60% Lineage Split"
              description="60% of all gross revenue routed to the Admiral. 30% Lineage. 10% EIGOO. Codified, not negotiated."
              tag="Sovereign Yield" />
            <PrizeCard icon={<Anchor size={28}/>} title="EIGOO Faculty Support"
              description="Operational backbone: private-label manufacturing access, video stream rails, course publishing, paid-ad doctrine."
              tag="Infrastructure" />
          </div>
        </div>
      </section>

      {/* VIRAL STRIKE */}
      <section className="relative py-20 px-6 md:px-12 lg:px-20" data-testid={LANDING.viralStrikePrize}>
        <div className="max-w-6xl mx-auto">
          <div className="glass p-8 md:p-12 border border-[#C9A227]/40 grid md:grid-cols-[1.2fr_1fr] gap-10 items-center">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <Flame className="text-[#C9A227]"/>
                <span className="label-gold">Viral Strike Bonus</span>
              </div>
              <h3 className="text-3xl md:text-4xl font-black uppercase tracking-tight text-white leading-tight">
                The <span className="gold-shimmer">most-upvoted</span> candidate seizes:
              </h3>
              <div className="mt-6 grid grid-cols-2 gap-6">
                <div>
                  <div className="text-4xl font-black gold-shimmer">$250</div>
                  <p className="text-white/60 text-xs tracking-widest uppercase mt-1">Boarding Credit</p>
                </div>
                <div>
                  <div className="text-4xl font-black gold-shimmer">$500</div>
                  <p className="text-white/60 text-xs tracking-widest uppercase mt-1">EIGOO Targeted Ad Spend</p>
                </div>
              </div>
              <p className="text-white/60 text-sm mt-6 max-w-md leading-relaxed">
                The community votes. Every vote is email-verified to keep the signal pure. Strongest pull wins this strike — independent of the final Admiral selection.
              </p>
              <Link to="/leaderboard" className="btn-outline-gold inline-block mt-7 text-xs">
                Cast Your Vote <ChevronRight size={14} className="inline -mt-0.5"/>
              </Link>
            </div>
            <div className="relative h-64 md:h-80 overflow-hidden border border-[#C9A227]/20">
              <img src={SKIN_PORTRAIT} alt="" className="w-full h-full object-cover"/>
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"/>
              <div className="absolute bottom-4 left-4 right-4">
                <p className="text-white text-xs tracking-[0.3em] uppercase">Public Leaderboard · Live</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RAFFLE */}
      <section className="relative py-24 px-6 md:px-12 lg:px-20" data-testid={LANDING.raffleSection}>
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <img src={GOLD_ABSTRACT} alt="" className="w-full h-full object-cover"/>
        </div>
        <div className="relative max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <Ticket className="text-[#C9A227]"/>
            <span className="label-gold">Armada Raffle</span>
          </div>
          <div className="grid md:grid-cols-[1fr_1.4fr] gap-10 items-start">
            <div>
              <h2 className="text-4xl md:text-5xl font-black uppercase tracking-tighter text-white leading-tight">
                Every Admiral Candidate <span className="gold-shimmer">earns a seat</span> at the table.
              </h2>
              <p className="text-white/60 mt-6 text-sm leading-relaxed">
                The moment your $99 Boarding Deposit clears, a unique <span className="text-[#C9A227] font-bold">Sovereign Raffle Ticket</span> is minted in your private candidate portal. Win the Admiralty or not — the Armada Raffle is your guaranteed shot at the cargo.
              </p>
              {contest?.raffle_drawing_at && (
                <div className="mt-8">
                  <CountdownTimer deadline={contest.raffle_drawing_at} label="Grand Drawing In" />
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {(contest?.raffle_prizes || []).map((p) => (
                <div key={p.rank} className="glass p-6 border border-[#C9A227]/25" style={{borderRadius:0}}>
                  <div className="flex items-baseline justify-between mb-2">
                    <span className="text-[10px] tracking-[0.3em] uppercase text-[#C9A227] font-bold">Rank #{p.rank}</span>
                    <span className="text-xs text-white/40 tabular-nums">${p.value_usd.toLocaleString()}</span>
                  </div>
                  <h4 className="text-white font-black uppercase tracking-tight">{p.title}</h4>
                  <p className="text-white/55 text-xs mt-2 leading-relaxed">{p.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ARMADA SECTOR CATALOG */}
      <section className="relative py-24 px-6 md:px-12 lg:px-20 border-t border-[#C9A227]/15" data-testid={LANDING.armadaSection}>
        <div className="max-w-6xl mx-auto">
          <div className="flex items-end justify-between gap-6 flex-wrap mb-10">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Compass className="text-[#C9A227]"/>
                <span className="label-gold">EIGOO Ecosystem · Armada Sector Catalog</span>
              </div>
              <h2 className="text-4xl md:text-6xl font-black uppercase tracking-tighter text-white leading-[0.95] max-w-3xl">
                The Armada is active across <span className="gold-shimmer">10 sectors</span>.
              </h2>
              <p className="text-white/60 mt-6 max-w-2xl">
                If you command a territory within these fleets — or beyond — launch your signal. EIGOO will architect the hull.
              </p>
            </div>
            <Link to="/armada" className="btn-outline-gold text-xs">
              Full Catalog <ChevronRight size={12} className="inline -mt-0.5"/>
            </Link>
          </div>
          <ArmadaSectorList />
        </div>
      </section>

      {/* LEADERBOARD TEASER */}
      <section className="relative py-24 px-6 md:px-12 lg:px-20">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-end justify-between mb-10 gap-4 flex-wrap">
            <div>
              <span className="label-gold">Live Leaderboard</span>
              <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tighter text-white mt-2">
                Public <span className="gold-shimmer">Signal</span>.
              </h2>
            </div>
            <Link to="/leaderboard" className="btn-outline-gold text-xs">
              See All <ChevronRight size={14} className="inline -mt-0.5"/>
            </Link>
          </div>
          {leaders.length === 0 ? (
            <div className="glass p-10 text-center border border-[#C9A227]/20">
              <p className="text-white/60 text-sm">
                The leaderboard is empty. <span className="text-[#C9A227]">Be the first to board.</span>
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-5">
              {leaders.slice(0, 3).map((c, i) => (
                <Link to={`/candidates/${c.id}`} key={c.id} className="glass p-6 border border-[#C9A227]/15 hover:border-[#C9A227]/60 transition-colors group">
                  <div className="flex items-baseline justify-between">
                    <span className="text-[10px] tracking-[0.3em] uppercase text-[#C9A227] font-bold">Rank #{i+1}</span>
                    <span className="text-white text-sm tabular-nums">▲ {c.upvotes}</span>
                  </div>
                  <h4 className="text-white text-xl font-black uppercase tracking-tight mt-3 group-hover:gold-shimmer">{c.name}</h4>
                  <p className="text-white/50 text-xs mt-2 truncate">{c.niche}</p>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* MODULES OVERVIEW */}
      <section className="relative py-24 px-6 md:px-12 lg:px-20">
        <div className="max-w-6xl mx-auto">
          <span className="label-gold">The Flagship Hull</span>
          <h2 className="text-4xl md:text-5xl font-black uppercase tracking-tighter text-white mt-3 mb-12 max-w-3xl">
            Five operational modules. <span className="gold-shimmer">Pre-built.</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            <ModuleCard to="/storefront" title="The Glow Storefront" tag="01 · Commerce" image={KIT_IMAGE}
              copy="Luxury e-commerce engine for private-label products and curated kits." />
            <ModuleCard to="/stream" title="Siren Stream" tag="02 · Broadcast" image={GOLD_ABSTRACT}
              copy="Integrated video and live-streaming platform for tutorial masterclasses." />
            <ModuleCard to="/college" title="Beauty War College" tag="03 · Academy" image={SKIN_PORTRAIT}
              copy="Course hosting engine with 3 EIGOO Standard curricula preloaded." />
            <ModuleCard to="/social" title="Social Command" tag="04 · Reach" image="https://images.pexels.com/photos/4889710/pexels-photo-4889710.jpeg"
              copy="TikTok / Instagram feeds with shoppable Signal Links." />
            <ModuleCard to="/leaderboard" title="Search for the Siren" tag="05 · Casting" image="https://images.pexels.com/photos/29611529/pexels-photo-29611529.jpeg"
              copy="Public contest, Viral Strike upvote engine, Armada Raffle." />
            <ModuleCard to="/admiral" title="Admiral Dashboard" tag="06 · Sovereign" image={HERO_BG}
              copy="Private portal: commerce, students, 75/15/10 Lineage Law split." ghost />
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-32 px-6 md:px-12 lg:px-20 border-t border-[#C9A227]/15">
        <div className="max-w-4xl mx-auto text-center">
          <Waves className="text-[#C9A227] mx-auto mb-6" size={32}/>
          <h2 className="text-4xl md:text-6xl font-black uppercase tracking-tighter text-white leading-tight">
            Claim the <span className="gold-shimmer">Commission</span>.
          </h2>
          <p className="text-white/60 mt-6 max-w-xl mx-auto">
            Boarding closes when the countdown does. The infrastructure is built. EIGOO is selecting the right leader to own the equity.
          </p>
          <button onClick={() => setBoardingOpen(true)} className="btn-gold mt-10" data-testid={LANDING.applyStickyBtn}>
            Apply for Admiral Commission
          </button>
        </div>
      </section>
    </Layout>
  );
}

const PrizeCard = ({ icon, title, description, tag }) => (
  <div className="glass p-7 border border-[#C9A227]/20 hover:border-[#C9A227]/60 transition-all relative group" data-testid={LANDING.prizeCard}>
    <div className="text-[#C9A227] mb-5">{icon}</div>
    <span className="absolute top-5 right-5 text-[9px] tracking-[0.3em] uppercase text-white/40 group-hover:text-[#C9A227]">{tag}</span>
    <h3 className="text-white text-xl md:text-2xl font-black uppercase tracking-tight leading-tight">{title}</h3>
    <p className="text-white/55 text-sm mt-3 leading-relaxed">{description}</p>
  </div>
);

const ModuleCard = ({ to, title, tag, image, copy, ghost }) => (
  <Link to={to} className={`relative overflow-hidden border ${ghost ? "border-[#C9A227]/50 bg-black" : "border-[#C9A227]/15"} hover:border-[#C9A227]/70 transition-all group h-72 block`}>
    <img src={image} alt="" className={`absolute inset-0 w-full h-full object-cover ${ghost ? "opacity-25" : "opacity-50"} group-hover:opacity-65 transition-opacity`}/>
    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent"/>
    <div className="relative h-full p-6 flex flex-col justify-end">
      <span className="text-[10px] tracking-[0.3em] uppercase text-[#C9A227] font-bold">{tag}</span>
      <h3 className="text-white text-2xl font-black uppercase tracking-tight mt-2">{title}</h3>
      <p className="text-white/60 text-xs mt-2 max-w-xs">{copy}</p>
      <div className="mt-4 text-[#C9A227] text-xs tracking-[0.2em] uppercase opacity-0 group-hover:opacity-100 transition-opacity">
        Enter Module <ChevronRight size={12} className="inline -mt-0.5"/>
      </div>
    </div>
  </Link>
);
